import type { ServicePageConfig } from "../../types";
import {
  getPackagePricingRows,
  PACKAGE_PRICE_HINT_SV,
  PACKAGE_PRICE_HINT_EN,
} from "../../package-pricing";
import { getCityMunicipalityList } from "../../cities";

const stockholmKommunerText = getCityMunicipalityList("stockholm");

export const solnaPages: ServicePageConfig[] = [
  {
    slug: "bortforsling-solna",
    serviceCategory: "bortforsling",
    city: "stockholm",

    meta: {
      title: "Bortforsling Solna – Snabb Hämtning | Ungdomskraft",
      description:
        "Boka bortforsling i Solna. Vi hämtar möbler, vitvaror och skräp i Råsunda, Hagalund, Huvudsta, Bergshamra, Arenastaden och Frösunda. Fast pris, ofta samma dag.",
      h1: "Bortforsling Solna",
    },

    hero: {
      heading: "Bortforsling i Solna",
      intro:
        "Boka bortforsling i Solna – vi hämtar möbler, vitvaror och skrymmande föremål i hela staden, från höghusen i Hagalund och de nya kvarteren i Arenastaden och Frösunda till radhusen i Bergshamra och Huvudsta. Snabb bokning, fast pris och ofta tillgängliga redan samma dag. Över 50 000 slutförda uppdrag i Stockholmsregionen.",
      heroImage: "/hero-bortforsling.webp",
      heroImageAlt:
        "Ungdomskraft hämtar möbler vid bortforsling i Solna – ung medarbetare lastar skåpbil",
    },

    itemsSection: {
      heading: "Saker vi bortforslar i Solna",
      items: [
        {
          name: "Återvinning",
          description: "Glas, metall, papper och annat återvinningsbart",
          image: "/recycling-bin.webp",
          alt: "Återvinning som hämtas vid bortforsling i Solna och körs till godkänd återvinningscentral",
        },
        {
          name: "Kartonger",
          description: "Flyttkartonger och wellpappkartonger",
          image: "/box.webp",
          alt: "Flyttkartonger som forslas bort från lägenhet i Råsunda, Solna",
        },
        {
          name: "Möbler",
          description: "Vardagsrum, sovrum, kontor och andra hemmöbler",
          image: "/furniture.webp",
          alt: "Soffor och hemmöbler vid bortforsling i Hagalund, Solna",
        },
        {
          name: "Elektronik",
          description:
            "TV-apparater, datorer, ljudsystem och annan elektronik",
          image: "/electronics.webp",
          alt: "Elektronik och TV-apparater som bortforslas i Arenastaden, Solna",
        },
        {
          name: "Vitvaror",
          description:
            "Kylskåp, tvättmaskiner, torktumlare och andra vitvaror",
          image: "/appliances.webp",
          alt: "Kylskåp och vitvaror som hämtas vid bortforsling i Huvudsta, Solna",
        },
        {
          name: "Träningsutrustning",
          description:
            "Löpband, cyklar, vikter och annan träningsutrustning",
          image: "/fitness-equipment.webp",
          alt: "Löpband och träningsutrustning vid bortforsling i Frösunda, Solna",
        },
      ],
    },

    seoContent: {
      mainHeading: "Bortforsling i Solna – snabbt, smidigt och ansvarsfullt",
      mainText:
        "Solna är en av Sveriges tätaste och mest snabbväxande städer – från de klassiska höghusen i Hagalund och lägenhetskvarteren i Råsunda och Huvudsta till de helt nya stadsdelarna Arenastaden och Frösunda. Den täta stadsmiljön gör bortforsling till en utmaning om man försöker klara den själv: trånga trapphus, små hissar, begränsad gatuparkering och många bostadsrättsföreningar med särskilda lastregler. Med Ungdomskraft slipper du allt krångel. Vi skickar unga, starka medarbetare med skåpbil direkt till din adress.\n\nVårt team hanterar hela kedjan: vi bär ut föremålen från din lägenhet, ditt radhus eller ditt förråd, lastar i bilen och kör till närmaste återvinningscentral. Solna är en av delägarkommunerna i SÖRAB, och förutom Returpunkt Solna Centrum och den mobila återvinningscentral som stannar i Huvudsta, Bergshamra och Hagalund kör vi det mesta till SÖRAB:s fullskaliga stationer i regionen, till exempel Hagby ÅVC i Täby. Du behöver inte tänka på sortering, parkering eller tunga lyft. Vi tar hand om allt.\n\nTack vare att E4 och E18 passerar i västra Solna och att Solnavägen, Frösundaleden och Råsundavägen binder ihop staden, tar vi oss snabbt mellan stadsdelarna. Det gör att vi ofta kan vara på plats i Solna redan samma dag du bokar. Våra medarbetare är vana vid både de höga punkthusen i Hagalund och de smala gatorna kring Gamla Råsunda – och har utrustningen som krävs: bärbälten, skyddsfiltar och lastramper som skyddar både dina ägodelar och trapphuset under hela processen.",
      preparationHeading: "Förbered inför din bortforsling i Solna",
      preparationText:
        "Du behöver egentligen inte göra mycket alls – våra ungdomar sköter det tunga jobbet. Men vill du snabba på processen och hålla nere kostnaden finns det några enkla saker du kan göra:\n\nRensa en fri väg (ungefär 1 meter bred) från entrén till föremålen som ska bort. Bor du i lägenhet i Hagalund, Råsunda eller Frösunda, kontrollera om det finns en lastzon, innergård eller gästparkering nära porten som vi kan använda för lastning – i den täta Solnabebyggelsen sparar det mycket tid jämfört med att leta plats på gatan. Bor du i radhus i Bergshamra eller Huvudsta är det smidigt om vi kan komma nära entrén eller garaget.\n\nSamla gärna sakerna på ett ställe om möjligt, till exempel i hallen eller förrådet. Du behöver inte demontera möbler – det ingår i vår tjänst om det behövs. Farligt avfall som färgrester, batterier och kemikalier kan vi tyvärr inte ta med, men sådant lämnar du som Solnabo gratis på Returpunkt Solna Centrum eller via stadens mobila miljöstation.",
      additionalSections: [
        {
          heading: "Stadsdelar och områden vi täcker i Solna",
          content:
            "Vi utför bortforsling i hela Solna stad – från de etablerade kvarteren till de nybyggda stadsdelarna. Oavsett om du bor i en lägenhet i Hagalund, ett radhus i Bergshamra, en nybyggd etta i Arenastaden eller en bostadsrätt i Råsunda kan vi vara på plats, ofta redan samma dag.\n\nI centrala Solna – Skytteholm med Solna centrum, Råsunda och Hagalund – har vi stor vana vid lägenhetshämtningar med hiss och trapphus, ofta i bostadsrättsföreningar med egna last- och parkeringsregler. Vi känner till de höga punkthusen i Hagalund och de tätare kvarteren längs Solnavägen och Frösundaleden, och planerar lastningen därefter.\n\nVi är minst lika snabba i stadens norra och västra delar: Frösunda och Arenastaden med sina nya bostadskvarter nära Solna station, Huvudsta ner mot Ulvsundasjön, Bergshamra vid Nationalstadsparken och Ulriksdal. Tack vare närheten till E4, E18, tvärbanan och tunnelbanans blå linje planerar vi alltid rutten i förväg så att du slipper vänta. Inga extra avgifter för någon stadsdel.",
        },
        {
          heading: "Återvinning och miljöansvar i Solna",
          content:
            "En viktig del av vår bortforsling i Solna är att allt hanteras på ett miljövänligt sätt. Vi sorterar materialet och kör det till godkända återvinningscentraler i regionen. Solna är en av delägarkommunerna i SÖRAB, och de återvinningslösningar vi använder mest för uppdrag i staden är:\n\nReturpunkt Solna Centrum – för mindre mängder och sådant som kan återbrukas, centralt beläget i Solna. SÖRAB:s mobila återvinningscentral – stannar regelbundet i Huvudsta, Bergshamra och Hagalund. Hagby ÅVC i Täby och övriga SÖRAB-stationer – dit vi kör större mängder grovavfall. Vi väljer alltid den lösning som ligger närmast din adress för att hålla restiden – och därmed kostnaden – nere.\n\nDet som kan återanvändas skänker vi vidare till organisationer som Myrorna och Stadsmissionen. På så sätt bidrar din bortforsling till cirkulär ekonomi – sakerna får ett nytt liv istället för att hamna på tippen.\n\nOm sakerna transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där allt körs till återvinningsstation ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
        },
        {
          heading: "Varför boka bortforsling i Solna med Ungdomskraft?",
          listItems: [
            {
              label: "Lokal kunskap –",
              text: "Vi känner Solnas stadsdelar, från punkthusen i Hagalund till de nya kvarteren i Arenastaden och Frösunda, och vet hur man löser lastning i tät stadsmiljö och bostadsrättsföreningar. Det gör att vi planerar effektiva rutter och håller nere kostnaden för dig.",
            },
            {
              label: "Snabb tillgänglighet –",
              text: "Vi har skåpbilar och personal i hela Stockholmsregionen och E4/E18 passerar i västra Solna. Ofta kan vi vara på plats redan samma dag du bokar – även på kvällar och helger.",
            },
            {
              label: "Fast pris utan överraskningar –",
              text: "Du ser priset direkt vid bokning. Inga dolda avgifter, inga tilläggsavgifter för någon stadsdel i Solna, och inga offertförfrågningar. Boka på två minuter.",
            },
            {
              label: "Försäkrat och tryggt –",
              text: "Alla uppdrag är försäkrade genom Trygg Hansa. Ditt trapphus, din innergård, dina väggar och dina ägodelar är skyddade under hela processen.",
            },
            {
              label: "Miljövänlig hantering –",
              text: "Vi sorterar och återvinner allt vi kan via SÖRAB:s återvinningslösningar och Returpunkt Solna Centrum. Det som går att donera skänks vidare. Du kan ha rätt till RUT-avdrag för transport till återbruk.",
            },
          ],
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar bortforsling i Solna?",
        answer:
          "Priset beror på volym, vikt och tid. Vår minsta kategori (Extra Liten) startar på 249 kr i grundavgift, och ett genomsnittligt uppdrag kostar runt 499–2 495 kr beroende på mängden. Du ser det exakta priset direkt vid bokning – inga dolda avgifter och inga tillägg för någon stadsdel i Solna.",
      },
      {
        question: "Kan jag få RUT-avdrag på bortforsling i Solna?",
        answer:
          "Om sakerna transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där allt körs till återvinningsstation ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
      },
      {
        question: "Hur snabbt kan ni komma för bortforsling i Solna?",
        answer:
          "Vi har skåpbilar och personal i hela Stockholmsregionen och E4/E18 passerar i västra Solna, så vi tar oss snabbt mellan stadsdelarna. Ofta kan vi vara på plats redan samma dag, och vid akuta behov ibland inom 60 minuter. Boka online och välj den tid som passar dig.",
      },
      {
        question: "Ingår det en skåpbil vid bortforsling i Solna?",
        answer:
          "Ja, vi kommer alltid med skåpbil och personal. Du behöver inte ordna eget fordon – vi sköter allt från bärning till transport och bortkörning till närmaste återvinningscentral.",
      },
      {
        question: "Kan ni bära ner saker från lägenhet i trappa i Solna?",
        answer:
          "Absolut. Vi är vana vid både trapphus och hissar i Solnas täta bebyggelse, från punkthusen i Hagalund till bostadsrätterna i Råsunda och Frösunda. Vi bär ner möbler, vitvaror och annat oavsett våning – inga extra avgifter för trappor.",
      },
      {
        question: "Kan ni hämta i bostadsrättsförening med särskilda lastregler i Solna?",
        answer:
          "Ja, vi är vana vid Solnas många bostadsrättsföreningar och deras regler för lastning, innergårdar och gästparkering. Meddela oss vid bokningen om det finns en särskild lastzon eller tider att ta hänsyn till, så planerar vi därefter.",
      },
      {
        question: "Kan ni demontera möbler innan bortforsling?",
        answer:
          "Ja, vi demonterar möbler på plats om det behövs för att få ut dem genom dörrar eller nedför trappor. Det ingår i tjänsten utan extra kostnad.",
      },
      {
        question: "Är bortforslingen i Solna försäkrad?",
        answer:
          "Ja, alla uppdrag är försäkrade genom Trygg Hansa. Det innebär att din fastighet, ditt trapphus, din innergård och dina ägodelar är skyddade under hela processen – från lyft till transport.",
      },
      {
        question: "Vart kör ni avfallet efter bortforsling i Solna?",
        answer:
          "Vi sorterar allt material och kör det till godkända återvinningscentraler. Solna är en av delägarkommunerna i SÖRAB, så mindre mängder lämnas på Returpunkt Solna Centrum medan större mängder grovavfall körs till SÖRAB-stationer som Hagby ÅVC i Täby. Det som kan återanvändas skänks till Myrorna eller Stadsmissionen.",
      },
      {
        question: "Kan jag boka bortforsling i Solna på helgen?",
        answer:
          "Ja, vi erbjuder bortforsling alla dagar i veckan, inklusive lördagar och söndagar. Välj det datum och den tid som passar dig bäst direkt i vår bokning.",
      },
      {
        question: "Kan ni hämta saker från källare eller förråd i Solna?",
        answer:
          "Ja, vi hämtar föremål från alla utrymmen – källare, förråd, vindsutrymmen och garage. Vi är vana vid de gemensamma förrådsutrymmena i Solnas bostadsrättsföreningar och flerbostadshus.",
      },
      {
        question: "Kan ni göra flera stopp vid bortforsling i Solna?",
        answer:
          "Ja, du kan lägga till flera hämtadresser i samma bokning. Det är praktiskt om du till exempel behöver hämta saker från både din lägenhet i Råsunda och ditt förråd på en annan adress i staden.",
      },
      {
        question: "Vilka stadsdelar i Solna täcker ni?",
        answer:
          "Vi utför bortforsling i hela Solna – Skytteholm med Solna centrum, Råsunda, Hagalund, Huvudsta, Bergshamra, Frösunda, Arenastaden, Ulriksdal, Haga och Järva. Inga extra avgifter för någon stadsdel.",
      },
      {
        question: "Tar ni hand om farligt avfall vid bortforsling i Solna?",
        answer:
          "Vi kan tyvärr inte transportera farligt avfall som färg, kemikalier, batterier eller asbest. Sådant lämnar du som Solnabo gratis på Returpunkt Solna Centrum eller via stadens mobila miljöstation.",
      },
      {
        question: "Vilka kommuner runt Solna utför ni bortforsling i?",
        answer: `Vi täcker hela Stockholmsregionen, inklusive följande kommuner: ${stockholmKommunerText}. Inga extra avgifter för kranskommuner.`,
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bortforsling,

    pricingRows: getPackagePricingRows("bortforsling", "sv"),

    dynamicHeadings: {
      threeSteps: "Boka din bortforsling i Solna i 3 enkla steg",
      step2Text:
        "Våra ungdomar i Solna kommer till dig och hämtar det du vill ha bortforslat. Vi hanterar tunga lyft, trånga trapphus, smala hissar och bostadsrättsföreningarnas lastregler – du behöver inte lyfta ett finger.",
      ctaHeading: "Boka bortforsling i Solna idag",
      ctaText:
        "Fyll i formuläret och få pris direkt – inga offertförfrågningar. Smidig bortforsling i hela Solna stad.",
    },

    en: {
      slug: "junk-removal-solna",
      meta: {
        title: "Junk Removal Solna – Fast Pickup | Ungdomskraft",
        description:
          "Book junk removal in Solna. We pick up furniture, appliances and bulky items in Råsunda, Hagalund, Huvudsta, Bergshamra, Arenastaden and Frösunda. Fixed price, often same day.",
        h1: "Junk Removal Solna",
      },
      hero: {
        heading: "Junk Removal in Solna",
        intro:
          "Book junk removal in Solna – we collect furniture, appliances and bulky items across the whole city, from the high-rises of Hagalund and the new quarters of Arenastaden and Frösunda to the townhouses of Bergshamra and Huvudsta. Quick booking, fixed price and often available the same day. Over 50,000 completed jobs in the Stockholm region.",
        heroImageAlt:
          "Ungdomskraft collecting furniture during junk removal in Solna – young worker loading a van",
      },
      itemsSection: {
        heading: "Items we remove in Solna",
        items: [
          {
            name: "Recycling",
            description: "Glass, metal, paper and other recyclables",
            image: "/recycling-bin.webp",
            alt: "Recyclables collected during junk removal in Solna and driven to an approved recycling centre",
          },
          {
            name: "Boxes",
            description: "Moving boxes and cardboard boxes",
            image: "/box.webp",
            alt: "Moving boxes removed from an apartment in Råsunda, Solna",
          },
          {
            name: "Furniture",
            description: "Living room, bedroom, office and other home furniture",
            image: "/furniture.webp",
            alt: "Sofas and home furniture during junk removal in Hagalund, Solna",
          },
          {
            name: "Electronics",
            description: "TVs, computers, sound systems and other electronics",
            image: "/electronics.webp",
            alt: "Electronics and TVs removed in Arenastaden, Solna",
          },
          {
            name: "Appliances",
            description: "Fridges, washing machines, dryers and other appliances",
            image: "/appliances.webp",
            alt: "Fridges and appliances collected during junk removal in Huvudsta, Solna",
          },
          {
            name: "Fitness Equipment",
            description: "Treadmills, bikes, weights and other exercise equipment",
            image: "/fitness-equipment.webp",
            alt: "Treadmill and fitness equipment during junk removal in Frösunda, Solna",
          },
        ],
      },
      seoContent: {
        mainHeading: "Junk removal in Solna – fast, hassle-free and responsible",
        mainText:
          "Solna is one of Sweden's densest and fastest-growing cities – from the classic high-rises of Hagalund and the apartment blocks of Råsunda and Huvudsta to the brand-new districts of Arenastaden and Frösunda. The dense urban setting makes junk removal a challenge if you try to do it yourself: narrow stairwells, small elevators, limited street parking and many housing cooperatives (bostadsrättsföreningar) with their own loading rules. With Ungdomskraft, you skip the hassle entirely. We send young, strong workers with a van directly to your address.\n\nOur team handles the entire chain: we carry items out of your apartment, townhouse or storage unit, load them into the van and drive to the nearest recycling centre. Solna is one of the part-owner municipalities of SÖRAB, and besides Returpunkt Solna Centrum and the mobile recycling centre that stops in Huvudsta, Bergshamra and Hagalund, we drive most bulky waste to SÖRAB's full-scale stations in the region, such as Hagby recycling centre in Täby. You don't need to worry about sorting, parking or heavy lifting. We take care of everything.\n\nBecause the E4 and E18 motorways pass through western Solna and Solnavägen, Frösundaleden and Råsundavägen tie the city together, we move quickly between districts. This means we can often be on site in Solna the same day you book. Our workers are used to both the tall point blocks of Hagalund and the narrow streets around Gamla Råsunda – and have the right equipment: carrying belts, protective blankets and loading ramps that protect both your belongings and the stairwell throughout the process.",
        preparationHeading: "Preparing for your junk removal in Solna",
        preparationText:
          "You don't really need to do much at all – our young workers handle the heavy work. But if you want to speed things up and keep the cost down, there are a few simple things you can do:\n\nClear a path (about 1 metre wide) from the entrance to the items that need to go. If you live in an apartment in Hagalund, Råsunda or Frösunda, check whether there is a loading zone, courtyard or visitor parking near the entrance we can use – in Solna's dense layout that saves a lot of time compared to searching for a spot on the street. If you live in a townhouse in Bergshamra or Huvudsta, it helps if we can get close to the entrance or garage.\n\nGather the items in one place if possible, for example in the hallway or storage unit. You don't need to disassemble furniture – we include that in our service if needed. We cannot take hazardous waste such as paint, batteries and chemicals, but as a Solna resident you can drop those off for free at Returpunkt Solna Centrum or via the city's mobile environmental station.",
        additionalSections: [
          {
            heading: "Districts and areas we cover in Solna",
            content:
              "We perform junk removal across the entire city of Solna – from the established quarters to the newly built districts. Whether you live in an apartment in Hagalund, a townhouse in Bergshamra, a new studio in Arenastaden or a cooperative flat in Råsunda, we can be on site, often the same day.\n\nIn central Solna – Skytteholm with Solna centrum, Råsunda and Hagalund – we are highly experienced with apartment pickups via elevator and stairwell, often in housing cooperatives with their own loading and parking rules. We know the tall point blocks of Hagalund and the denser quarters along Solnavägen and Frösundaleden, and plan the loading accordingly.\n\nWe are just as quick in the northern and western parts of the city: Frösunda and Arenastaden with their new residential blocks near Solna station, Huvudsta down towards Lake Ulvsundasjön, Bergshamra by the Royal National City Park, and Ulriksdal. Thanks to the proximity of the E4, E18, the tvärbanan tram and the blue metro line, we always plan the route in advance so you don't have to wait. No extra charges for any district.",
          },
          {
            heading: "Recycling and environmental responsibility in Solna",
            content:
              "An important part of our junk removal in Solna is that everything is handled in an environmentally responsible way. We sort the materials and drive them to approved recycling centres in the region. Solna is one of the part-owner municipalities of SÖRAB, and the recycling solutions we use most for jobs in the city are:\n\nReturpunkt Solna Centrum – for smaller amounts and items that can be reused, centrally located in Solna. SÖRAB's mobile recycling centre – which stops regularly in Huvudsta, Bergshamra and Hagalund. Hagby recycling centre in Täby and other SÖRAB stations – where we take larger amounts of bulky waste. We always choose the solution nearest your address to keep travel time – and therefore the cost – down.\n\nItems that can be reused are donated to organisations like Myrorna and Stadsmissionen. This way, your junk removal contributes to a circular economy – items get a new life instead of ending up in landfill.\n\nIf the items are transported to a charity shop or organisation for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. Standard junk removal where everything goes to a recycling station does not qualify. The deduction is handled directly on the invoice.",
          },
          {
            heading: "Why book junk removal in Solna with Ungdomskraft?",
            listItems: [
              {
                label: "Local knowledge –",
                text: "We know Solna's districts, from the point blocks of Hagalund to the new quarters of Arenastaden and Frösunda, and how to solve loading in a dense urban environment and in housing cooperatives. This lets us plan efficient routes and keep the cost down for you.",
              },
              {
                label: "Fast availability –",
                text: "We have vans and staff across the Stockholm region and the E4/E18 passes through western Solna. We can often be on site the same day you book – including evenings and weekends.",
              },
              {
                label: "Fixed price with no surprises –",
                text: "You see the price immediately when booking. No hidden fees, no surcharges for any district in Solna, and no quote requests. Book in two minutes.",
              },
              {
                label: "Insured and safe –",
                text: "All jobs are insured through Trygg Hansa. Your stairwell, your courtyard, your walls and your belongings are protected throughout the entire process.",
              },
              {
                label: "Eco-friendly handling –",
                text: "We sort and recycle everything we can via SÖRAB's recycling solutions and Returpunkt Solna Centrum. Items suitable for donation are passed on. You may be entitled to a RUT tax deduction for transport to reuse.",
              },
            ],
          },
        ],
      },
      faq: [
        {
          question: "How much does junk removal in Solna cost?",
          answer:
            "The price depends on volume, weight and time. Our smallest category (Extra Liten) starts at 249 kr base fare, and an average job costs around 499–1,699 kr depending on the amount. You see the exact price immediately when booking – no hidden fees and no surcharges for any district in Solna.",
        },
        {
          question: "Can I get a RUT tax deduction on junk removal in Solna?",
          answer:
            "If the items are transported to a charity shop or organisation for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. Standard junk removal where everything goes to a recycling station does not qualify. The deduction is handled directly on the invoice.",
        },
        {
          question: "How quickly can you come for junk removal in Solna?",
          answer:
            "We have vans and staff across the Stockholm region and the E4/E18 passes through western Solna, so we move quickly between districts. We can often be on site the same day, and for urgent needs sometimes within 60 minutes. Book online and choose the time that suits you.",
        },
        {
          question: "Is a van included with junk removal in Solna?",
          answer:
            "Yes, we always come with a van and crew. You don't need to arrange your own vehicle – we handle everything from carrying to transport and disposal at the nearest recycling centre.",
        },
        {
          question: "Can you carry items down from an apartment via stairs in Solna?",
          answer:
            "Absolutely. We are used to both stairwells and elevators in Solna's dense buildings, from the point blocks of Hagalund to the cooperative flats in Råsunda and Frösunda. We carry furniture, appliances and other items regardless of floor – no extra charges for stairs.",
        },
        {
          question: "Can you collect from a housing cooperative with special loading rules in Solna?",
          answer:
            "Yes, we are used to Solna's many housing cooperatives (bostadsrättsföreningar) and their rules for loading, courtyards and visitor parking. Let us know at booking if there is a specific loading zone or times to consider, and we plan accordingly.",
        },
        {
          question: "Can you disassemble furniture before removal?",
          answer:
            "Yes, we disassemble furniture on site if needed to get it through doors or down stairs. This is included in the service at no extra cost.",
        },
        {
          question: "Is junk removal in Solna insured?",
          answer:
            "Yes, all jobs are insured through Trygg Hansa. This means your property, stairwell, courtyard and belongings are protected throughout the entire process – from lifting to transport.",
        },
        {
          question: "Where do you take the waste after junk removal in Solna?",
          answer:
            "We sort all materials and drive them to approved recycling centres. Solna is one of the part-owner municipalities of SÖRAB, so smaller amounts go to Returpunkt Solna Centrum while larger amounts of bulky waste go to SÖRAB stations like Hagby recycling centre in Täby. Items that can be reused are donated to Myrorna or Stadsmissionen.",
        },
        {
          question: "Can I book junk removal in Solna on weekends?",
          answer:
            "Yes, we offer junk removal every day of the week, including Saturdays and Sundays. Choose the date and time that works best for you directly in our booking.",
        },
        {
          question: "Can you collect items from basements or storage units in Solna?",
          answer:
            "Yes, we pick up items from all spaces – basements, storage units, attics and garages. We are used to the shared storage rooms in Solna's housing cooperatives and apartment buildings.",
        },
        {
          question: "Can you make multiple stops during junk removal in Solna?",
          answer:
            "Yes, you can add multiple pickup addresses in the same booking. This is practical if you need to collect items from both your apartment in Råsunda and a storage unit at another address in the city, for example.",
        },
        {
          question: "Which districts in Solna do you cover?",
          answer:
            "We perform junk removal across all of Solna – Skytteholm with Solna centrum, Råsunda, Hagalund, Huvudsta, Bergshamra, Frösunda, Arenastaden, Ulriksdal, Haga and Järva. No extra charges for any district.",
        },
        {
          question: "Do you handle hazardous waste during junk removal in Solna?",
          answer:
            "Unfortunately, we cannot transport hazardous waste such as paint, chemicals, batteries or asbestos. As a Solna resident you can drop those off for free at Returpunkt Solna Centrum or via the city's mobile environmental station.",
        },
        {
          question: "Which municipalities around Solna do you serve?",
          answer:
            "We cover the entire Stockholm region, including Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. No extra charges for surrounding municipalities.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.bortforsling,
      pricingRows: getPackagePricingRows("bortforsling", "en"),
      dynamicHeadings: {
        threeSteps: "Book your junk removal in Solna in 3 easy steps",
        step2Text:
          "Our young workers in Solna come to you and collect whatever you want removed. We handle heavy lifting, tight staircases, narrow elevators and housing-cooperative loading rules – you don't have to lift a finger.",
        ctaHeading: "Book junk removal in Solna today",
        ctaText:
          "Fill in the form and get an instant price – no quote requests. Easy junk removal across the whole city of Solna.",
      },
    },
  },
];
